/**
 * InstitutionalInvoiceDrawer (Plan 14).
 *
 * Right-side detail drawer opened from the invoice table. Shows:
 *
 *   - Summary header (status chip + due / total / outstanding)
 *   - Summary rollup — Location → Mealtime → Meal Plan → Eligibility with a per-campus
 *     tax row and the totals block, rendered from `detail.document`: the
 *     same numbers the downloadable summary PDF prints
 *   - Payment history (credit notes today; per-attempt history lands
 *     when the apps/customers reconciliation surface ships)
 *   - Actions row: "Stripe invoice PDF" (the Stripe-hosted PDF / URL),
 *     "Summary PDF" + "Item manifest CSV" (Ordo's own documents, built
 *     from the invoice's frozen lines), "Pay by check" (opens the
 *     PayByCheckSheet), "Pay now" (opens the Stripe-hosted invoice URL)
 *
 * The "Issue credit memo" action is intentionally NOT here — it
 * belongs to apps/customers per the Plan-14 split (ORDO_ADMIN
 * surface). The drawer omits the button entirely instead of
 * conditionally rendering, so the customer-side affordance set stays
 * stable.
 */

import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Drawer, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from '@ordo/ui/drawer'
import { Separator } from '@ordo/ui/separator'
import { Skeleton } from '@ordo/ui/skeleton'
import { toast } from '@ordo/ui/sonner'
import { Ban, CreditCard, FileDown, FileSpreadsheet, FileText, ReceiptText } from 'lucide-react'
import { Fragment, useState } from 'react'

import {
  type SchoolARInvoiceDetail,
  type SchoolARInvoiceDocument,
  type SchoolARInvoiceRow,
  useCancelPayByCheckMutation,
  usePayNowMutation,
  useSchoolARInvoiceDetailQuery,
} from '@/api/billing-school'
import { cn } from '@/lib/utils'

import { PayByCheckSheet } from './pay-by-check-sheet'
import {
  bucketLabel,
  formatCents,
  formatDate,
  formatTaxRate,
  invoiceStatusLabel,
  invoiceStatusTone,
  isAgingEligible,
  offlineMethodLabel,
  outstandingCents,
  paymentStateDetail,
  paymentStateLabel,
} from './school-ar-helpers'

export interface InstitutionalInvoiceDrawerProps {
  open: boolean
  invoiceId: string | null
  /** Pre-loaded row from the table — used for the header while detail loads. */
  preloadRow?: SchoolARInvoiceRow | null
  /** The page's summary-PDF download (fetch + save + toast) — the same one
   *  the table's row menu runs, so both surfaces behave identically. */
  onDownloadSummaryPdf: (row: SchoolARInvoiceRow) => void
  onDownloadManifestCsv: (row: SchoolARInvoiceRow) => void
  onOpenChange: (open: boolean) => void
}

export function InstitutionalInvoiceDrawer({
  open,
  invoiceId,
  preloadRow,
  onDownloadSummaryPdf,
  onDownloadManifestCsv,
  onOpenChange,
}: InstitutionalInvoiceDrawerProps) {
  const { data, isPending, error } = useSchoolARInvoiceDetailQuery(open ? invoiceId : null)
  const [checkSheetOpen, setCheckSheetOpen] = useState(false)
  const payNowMutation = usePayNowMutation()
  const cancelCheckMutation = useCancelPayByCheckMutation()

  const invoice = data?.invoice ?? preloadRow ?? null
  const hasPendingCheck = invoice?.checkPayment?.status === 'SUBMITTED'

  const handlePayNow = async () => {
    if (!invoice) {
      return
    }
    try {
      const result = await payNowMutation.mutateAsync(invoice.id)
      if (result.stripeHostedUrl) {
        window.open(result.stripeHostedUrl, '_blank', 'noopener,noreferrer')
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      if (message.includes('PAY_NOW_NOT_READY')) {
        toast.info('Invoice is not ready to pay yet')
      } else {
        toast.error('Could not start payment', { description: message })
      }
    }
  }

  const handleCancelCheck = async () => {
    const checkPaymentId = invoice?.checkPayment?.id
    if (!checkPaymentId) {
      return
    }
    try {
      await cancelCheckMutation.mutateAsync(checkPaymentId)
      toast.success('Check submission canceled.')
    } catch (err) {
      toast.error('Could not cancel check submission', {
        description: err instanceof Error ? err.message : String(err),
      })
    }
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="right">
      <DrawerContent className="ml-auto h-full w-full max-w-3xl rounded-none border-l">
        <DrawerHeader className="border-b">
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-col gap-1">
              <DrawerTitle>Invoice {invoice ? invoice.id.slice(0, 8) : ''}</DrawerTitle>
              <DrawerDescription>
                {invoice ? (
                  <>
                    {invoice.organizationName}
                    {invoice.schoolLocationName ? ` · ${invoice.schoolLocationName}` : ''}
                  </>
                ) : null}
              </DrawerDescription>
            </div>
            {invoice ? (
              <Badge variant={invoiceStatusTone(invoice.status)}>{invoiceStatusLabel(invoice.status)}</Badge>
            ) : null}
          </div>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isPending && !data ? (
            <DetailSkeleton />
          ) : error ? (
            <p className="text-sm text-destructive">{error.message}</p>
          ) : data ? (
            <DetailBody detail={data} />
          ) : invoice ? (
            <SummarySection row={invoice} />
          ) : null}
        </div>

        <DrawerFooter className="border-t">
          <div className="flex flex-wrap items-center justify-end gap-2">
            {invoice?.stripeInvoiceRef ? (
              <Button variant="outline" asChild>
                <a
                  href={data?.stripeInvoicePdfUrl ?? data?.stripeHostedUrl ?? '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-disabled={!data?.stripeInvoicePdfUrl && !data?.stripeHostedUrl}>
                  <FileDown className="mr-1.5 h-4 w-4" aria-hidden />
                  Stripe invoice PDF
                </a>
              </Button>
            ) : null}
            {invoice ? (
              <>
                <Button variant="outline" onClick={() => onDownloadSummaryPdf(invoice)}>
                  <FileText className="mr-1.5 h-4 w-4" aria-hidden />
                  Summary PDF
                </Button>
                <Button variant="outline" onClick={() => onDownloadManifestCsv(invoice)}>
                  <FileSpreadsheet className="mr-1.5 h-4 w-4" aria-hidden />
                  Item manifest CSV
                </Button>
              </>
            ) : null}
            <Button
              variant="outline"
              onClick={() => setCheckSheetOpen(true)}
              disabled={
                !invoice ||
                hasPendingCheck ||
                // Payable only once billed and unsettled — the server's
                // pay-by-check and pay-now routes refuse everything else.
                !isAgingEligible(invoice.status)
              }>
              <ReceiptText className="mr-1.5 h-4 w-4" aria-hidden />
              {hasPendingCheck ? 'Payment submitted' : 'Pay by check'}
            </Button>
            {hasPendingCheck ? (
              <Button variant="outline" onClick={handleCancelCheck} disabled={cancelCheckMutation.isPending}>
                <Ban className="mr-1.5 h-4 w-4" aria-hidden />
                {cancelCheckMutation.isPending ? 'Canceling…' : 'Cancel check'}
              </Button>
            ) : null}
            <Button
              onClick={handlePayNow}
              disabled={
                payNowMutation.isPending ||
                !invoice ||
                hasPendingCheck ||
                // Payable only once billed and unsettled — the server's
                // pay-by-check and pay-now routes refuse everything else.
                !isAgingEligible(invoice.status)
              }>
              <CreditCard className="mr-1.5 h-4 w-4" aria-hidden />
              {payNowMutation.isPending ? 'Starting…' : 'Pay now'}
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>

      {invoice ? <PayByCheckSheet open={checkSheetOpen} onOpenChange={setCheckSheetOpen} invoice={invoice} /> : null}
    </Drawer>
  )
}

function SummarySection({ row }: { row: SchoolARInvoiceRow }) {
  const outstanding = outstandingCents(row)
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <SummaryCell label="Period start" value={formatDate(row.periodStart)} />
        <SummaryCell label="Period end" value={formatDate(row.periodEnd)} />
        <SummaryCell label="Due" value={formatDate(row.dueAt)} />
        <SummaryCell label="Status" value={invoiceStatusLabel(row.status)} />
        <SummaryCell label="Total" value={formatCents(row.totalCents, row.currency)} />
        <SummaryCell label="Paid" value={formatCents(row.paidCents, row.currency)} />
        <SummaryCell
          label="Outstanding"
          value={formatCents(outstanding, row.currency)}
          tone={outstanding > 0 ? 'destructive' : 'neutral'}
        />
        <SummaryCell label="Bucket" value={outstanding > 0 ? bucketLabel(row.bucket) : '—'} />
      </div>
      {row.checkPayment ? (
        <div className="rounded-md border border-border bg-muted/30 p-3 text-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-medium">
              {/* Never hardcode "Check" here — the same record now carries
                  ACH/wire/cash, and a wire labelled "Check received" is a
                  reconciliation error the school has to phone in about. */}
              {paymentStateLabel(row.paymentState)}
            </span>
            <span className="tabular-nums">
              {formatCents(
                row.checkPayment.confirmedAmountCents ?? row.checkPayment.submittedAmountCents,
                row.checkPayment.currency,
              )}
            </span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {paymentStateDetail(row) ??
              `${offlineMethodLabel(row.checkPayment.method)} · mailed ${formatDate(row.checkPayment.mailedAt)}`}
          </p>
        </div>
      ) : null}
    </div>
  )
}

function SummaryCell({ label, value, tone }: { label: string; value: string; tone?: 'destructive' | 'neutral' }) {
  return (
    <div className="flex flex-col gap-0.5">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p
        className={
          tone === 'destructive'
            ? 'text-sm font-semibold tabular-nums text-destructive'
            : 'text-sm font-semibold tabular-nums'
        }>
        {value}
      </p>
    </div>
  )
}

function DetailBody({ detail }: { detail: SchoolARInvoiceDetail }) {
  return (
    <div className="flex flex-col gap-6">
      <SummarySection row={detail.invoice} />

      <Separator />

      <section>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Summary</h3>
        <InvoiceRollup document={detail.document} />
      </section>

      <Separator />

      <section>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Payment history</h3>
        {detail.creditNotes.length === 0 && detail.checkPayments.length === 0 ? (
          <p className="text-xs text-muted-foreground">No adjustments yet.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {detail.checkPayments.map((payment) => (
              <li
                key={payment.id}
                className="flex items-start justify-between gap-3 rounded-md border border-border bg-muted/30 p-3">
                <div className="flex flex-col gap-0.5">
                  <p className="text-sm font-medium">
                    Check #{payment.checkNumber} · {payment.status.toLowerCase()}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Mailed {formatDate(payment.mailedAt)}
                    {payment.receivedAt ? ` · received ${formatDate(payment.receivedAt)}` : ''}
                  </p>
                  {payment.memo ? <p className="text-xs text-muted-foreground">{payment.memo}</p> : null}
                </div>
                <p className="font-medium tabular-nums">
                  {formatCents(payment.confirmedAmountCents ?? payment.submittedAmountCents, payment.currency)}
                </p>
              </li>
            ))}
            {detail.creditNotes.map((cn) => (
              <li
                key={cn.id}
                className="flex items-start justify-between gap-3 rounded-md border border-border bg-muted/30 p-3">
                <div className="flex flex-col gap-0.5">
                  <p className="text-sm font-medium">Credit note · {cn.status}</p>
                  <p className="text-xs text-muted-foreground">{cn.reason}</p>
                  <p className="text-xs text-muted-foreground">{formatDate(cn.createdAt)}</p>
                </div>
                <p className="font-medium tabular-nums">{formatCents(cn.amountCents, cn.currency)}</p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

/**
 * The Location → Mealtime → Meal Plan → Eligibility rollup the summary PDF prints, with
 * the same per-campus tax row and totals block, so what the school reads
 * here is what the downloaded document says. Presentation only: every
 * number is in `document`, which the server rolled up from the frozen lines.
 */
function InvoiceRollup({ document: doc }: { document: SchoolARInvoiceDocument }) {
  // Empty only when there is truly nothing: an invoice whose only frozen
  // lines are a reconciling or adjustment line has no campus rows but a
  // real Total, and the PDF prints that totals block — so must this.
  const { totals } = doc
  const nothingBilled =
    doc.campuses.length === 0 &&
    totals.charges.length === 0 &&
    totals.deductionsCents === 0 &&
    totals.taxCents === 0 &&
    totals.totalCents === 0
  if (nothingBilled) {
    return <p className="text-xs text-muted-foreground">No invoice lines were captured for this invoice.</p>
  }
  const money = (cents: number) => formatCents(cents, doc.currency)
  /** `($12.00)` — the amount reduces the bill, as on the PDF. */
  const reduces = (cents: number) => `(${money(cents)})`
  /**
   * The Rate cell: an em dash when the node spans more than one price. Only
   * a LEAF is guaranteed to carry a single rate (the roll-up splits leaves
   * per rate), so a dash always has its detail on the rows below it.
   */
  const rate = (node: { unitPriceCents: number | null }) =>
    node.unitPriceCents === null ? '—' : money(node.unitPriceCents)
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-border text-xs text-muted-foreground">
          <th className="py-1.5 text-left font-medium">Values</th>
          <th className="py-1.5 text-right font-medium">Rate</th>
          <th className="py-1.5 text-right font-medium">Quantity</th>
          <th className="py-1.5 text-right font-medium">Amount</th>
        </tr>
      </thead>
      <tbody>
        {doc.campuses.map((campus) => (
          <Fragment key={campus.campusId ?? campus.campusName}>
            <RollupRow
              label={campus.campusName}
              rate={rate(campus)}
              amount={money(campus.amountCents)}
              quantity={campus.quantity}
              className="border-t border-border font-semibold"
            />
            {campus.mealtimes.map((mealtime) => (
              <Fragment key={mealtime.key}>
                <RollupRow
                  label={mealtime.label}
                  rate={rate(mealtime)}
                  amount={money(mealtime.amountCents)}
                  quantity={mealtime.quantity}
                  indent={1}
                  className="bg-muted/30"
                />
                {/* Always three levels, as on the PDF: a plan-less bulk line reads "School Order".
                    The fourth — the eligibility split — is present only where a plan has more
                    than one tier; otherwise the plan row arrives already collapsed. */}
                {mealtime.children.map((plan) => (
                  <Fragment key={plan.key}>
                    <RollupRow
                      label={plan.label}
                      rate={rate(plan)}
                      amount={money(plan.amountCents)}
                      quantity={plan.quantity}
                      indent={2}
                    />
                    {plan.children.map((tier) => (
                      <RollupRow
                        key={tier.key}
                        label={tier.label}
                        rate={rate(tier)}
                        amount={money(tier.amountCents)}
                        quantity={tier.quantity}
                        indent={3}
                      />
                    ))}
                  </Fragment>
                ))}
              </Fragment>
            ))}
            <RollupRow
              label={`Taxes (${formatTaxRate(campus.taxRateBps)})`}
              amount={money(campus.taxCents)}
              className="border-b border-border font-semibold"
            />
          </Fragment>
        ))}
        <RollupRow label="Subtotal" amount={money(totals.subtotalCents)} />
        <RollupRow label="Taxes" amount={money(totals.taxCents)} />
        {/* Discounts always render, even at zero, so two months look alike;
            the rows below are exceptions and appear only when non-zero. */}
        <RollupRow label="Discounts" amount={totals.discountCents === 0 ? money(0) : reduces(totals.discountCents)} />
        {totals.charges.map((charge) => (
          <RollupRow key={charge.key} label={charge.label} amount={money(charge.amountCents)} />
        ))}
        {totals.deductionsCents !== 0 ? (
          <RollupRow label="Deductions" amount={reduces(totals.deductionsCents)} />
        ) : null}
        <RollupRow label="Total" amount={money(totals.totalCents)} className="font-semibold" />
        {/* Memo rows, as on the PDF: context for the Total, never part of
            it. Only when families actually paid something — a bulk-ordering
            school has no residual to explain. */}
        {totals.familyPaidCents > 0 ? (
          <>
            <RollupRow
              label="Meals served (total value)"
              amount={money(totals.grossCents)}
              className="border-t border-border text-muted-foreground"
            />
            <RollupRow
              label="Paid directly by families"
              amount={money(totals.familyPaidCents)}
              className="text-muted-foreground"
            />
          </>
        ) : null}
      </tbody>
    </table>
  )
}

const ROLLUP_INDENT = ['', 'pl-5', 'pl-10', 'pl-15'] as const

function RollupRow({
  label,
  amount,
  quantity,
  rate,
  indent = 0,
  className,
}: {
  label: string
  amount: string
  /** Omitted on tax and totals rows, as on the PDF. */
  quantity?: number
  /** Omitted on tax and totals rows: neither has a per-unit price. */
  rate?: string
  indent?: 0 | 1 | 2 | 3
  className?: string
}) {
  return (
    <tr className={className}>
      <td className={cn('py-1.5 pr-3', ROLLUP_INDENT[indent])}>{label}</td>
      <td className="py-1.5 text-right tabular-nums">{rate ?? ''}</td>
      <td className="py-1.5 text-right tabular-nums">{quantity ?? ''}</td>
      <td className="py-1.5 text-right tabular-nums">{amount}</td>
    </tr>
  )
}

function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex flex-col gap-1">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-4 w-24" />
          </div>
        ))}
      </div>
      <Skeleton className="h-px w-full" />
      {/* Summary rollup: heading, then campus / mealtime / tax rows and the
          totals block — one label + one amount per row, like the table. */}
      <div className="flex flex-col gap-2">
        <Skeleton className="h-3 w-20" />
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex items-center justify-between gap-3">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </div>
      <Skeleton className="h-px w-full" />
      <Skeleton className="h-16 w-full rounded-md" />
    </div>
  )
}
